import React, { useState, useRef, useEffect, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Navbar from './Navbar';
import AuthModal from './AuthModal';

const AIInterviewSimulator = () => {
  const { isAuthenticated } = useAuth();

  
  // Scroll to top on component mount
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo(0, 0);
  }, []);
  
  // Interview state
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [isStartingInterview, setIsStartingInterview] = useState(false);
  const [difficulty, setDifficulty] = useState('MEDIUM');
  const [focus] = useState('Data Structures and Algorithms'); // Fixed to DSA only
  const [, setSessionId] = useState(null);
  const [problem, setProblem] = useState('');
  const [startTime, setStartTime] = useState(null);
  
  // Code editor
  const [code, setCode] = useState('');
  const [language, setLanguage] = useState('javascript');
  
  // Chat
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [lastInteractionTime, setLastInteractionTime] = useState(Date.now());
  
  // Evaluation
  const [showEvaluation, setShowEvaluation] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [isEvaluating, setIsEvaluating] = useState(false);
  
  // Interview credits
  const [interviewCredits, setInterviewCredits] = useState({
    remainingFreeInterviews: 1,
    paidCredits: 0,
    hasAvailableCredits: true
  });
  const [interviewConfig, setInterviewConfig] = useState({
    basePrice: 299,
    gstRate: 0.18,
    paidCreditsPerPack: 5,
    freeInterviewsLimit: 1
  });
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [usedFreeInterview, setUsedFreeInterview] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const paidPackCredits = interviewConfig.paidCreditsPerPack || 5;
  const basePrice = interviewConfig.basePrice ?? 299;
  const gstRate = interviewConfig.gstRate ?? 0.18;
  const gstAmount = Math.round(basePrice * gstRate);
  const totalPrice = basePrice + gstAmount;
  const gstPercent = Math.round(gstRate * 100);
  
  // Resizing - 3 panel layout
  const [problemWidth, setProblemWidth] = useState(25); // % of screen
  const [chatWidth, setChatWidth] = useState(30); // % of screen
  const [isResizingProblem, setIsResizingProblem] = useState(false);
  const [isResizingChat, setIsResizingChat] = useState(false);
  
  // Mobile state
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 640);
  const [activeMobileTab, setActiveMobileTab] = useState('problem'); // 'problem', 'chat', 'editor'
  // eslint-disable-next-line no-unused-vars
  const [showDifficultyDropdown, setShowDifficultyDropdown] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  
  // Auth modals
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  
  // Navigation warning modal
  const [showNavigationWarning, setShowNavigationWarning] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  
  // Mobile warning popup
  const [showMobileWarning, setShowMobileWarning] = useState(false);
  
  const chatEndRef = useRef(null);
  const editorRef = useRef(null);
  
  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 640;
      setIsMobile(mobile);
      // Close dropdowns when switching between mobile/desktop
      if (!mobile) {
        setShowDifficultyDropdown(false);
        setShowLanguageDropdown(false);
      }
    };
    
    // Set initial mobile state
    setIsMobile(window.innerWidth <= 640);
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);


  // Show mobile warning when interview starts on mobile (always show, don't remember dismissal)
  useEffect(() => {
    if (interviewStarted && isMobile) {
      // Always show the warning when interview starts on mobile
      // Small delay to ensure UI is ready
      setTimeout(() => {
        setShowMobileWarning(true);
      }, 500);
    }
  }, [interviewStarted, isMobile]);

  // Problem panel resize handler
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizingProblem) return;
      const newWidthPercent = (e.clientX / window.innerWidth) * 100;
      if (newWidthPercent >= 15 && newWidthPercent <= 40) {
        setProblemWidth(newWidthPercent);
      }
    };

    const handleMouseUp = () => {
      setIsResizingProblem(false);
    };

    if (isResizingProblem) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizingProblem]);

  // Chat panel resize handler
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizingChat) return;
      const newWidthPercent = (e.clientX / window.innerWidth) * 100;
      const minChatWidth = problemWidth + 15;
      const maxChatWidth = 70;
      if (newWidthPercent >= minChatWidth && newWidthPercent <= maxChatWidth) {
        setChatWidth(newWidthPercent - problemWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizingChat(false);
    };

    if (isResizingChat) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizingChat, problemWidth]);

  // Load interview credits on mount
  useEffect(() => {
    const loadCredits = async () => {
      if (isAuthenticated()) {
        try {
          const token = localStorage.getItem('token');
          const response = await api.get('/api/ai-assistant/interview/credits', {
            headers: { Authorization: `Bearer ${token}` }
          });
          setInterviewCredits(response.data);
          setInterviewConfig(prev => ({
            ...prev,
            basePrice: response.data.basePrice ?? prev.basePrice,
            gstRate: response.data.gstRate ?? prev.gstRate,
            paidCreditsPerPack: response.data.paidCreditsPerPack ?? prev.paidCreditsPerPack,
            freeInterviewsLimit: response.data.freeInterviewsLimit ?? prev.freeInterviewsLimit
          }));
        } catch (error) {
          console.error('Failed to load interview credits:', error);
        }
      }
    };
    loadCredits();
  }, [isAuthenticated]);

  const startInterview = async () => {
    // Check if user is authenticated
    if (!isAuthenticated()) {
      setShowLoginPrompt(true);
      return;
    }
    
    setIsStartingInterview(true);
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(
        '/api/ai-assistant/interview/start',
        { difficulty, focus, language }, // Include language
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update credits from response
      if (response.data.remainingFreeInterviews !== undefined) {
        setInterviewCredits({
          remainingFreeInterviews: response.data.remainingFreeInterviews,
          paidCredits: response.data.paidCredits,
          hasAvailableCredits: (response.data.remainingFreeInterviews > 0 || response.data.paidCredits > 0)
        });
        setUsedFreeInterview(response.data.usedFreeInterview || false);
      }

      setProblem(response.data.problem);
      setSessionId(response.data.sessionId);
      setInterviewStarted(true);
      setStartTime(Date.now()); // Track start time
      
      // Add initial interviewer greeting and question
      setMessages([
        {
          role: 'interviewer',
          content: `Hi! I'm excited to interview you today. I'm going to present you with a ${difficulty.toLowerCase()} level coding problem focused on ${focus}.\n\nBefore we dive in, tell me: Have you worked with ${focus} problems before? What's your comfort level with ${language}?\n\nAlso, I'd love to see your problem-solving approach, so please think out loud as you work through this! Feel free to ask clarifying questions anytime.\n\nReady to begin?`,
          timestamp: new Date()
        }
      ]);

      // Add a reminder after 5 seconds
      setTimeout(() => {
        setMessages(prev => [...prev, {
          role: 'interviewer',
          content: `Great! Take a moment to read through the problem on the left. Once you've had a chance to review it, walk me through your initial thoughts. What approach are you considering? Are there any clarifying questions about the problem?`,
          timestamp: new Date()
        }]);
        setLastInteractionTime(Date.now());
      }, 5000);

      // Set initial code template
      const templates = {
        javascript: '// Write your solution here\nfunction solution() {\n  \n}\n',
        python: '# Write your solution here\ndef solution():\n    pass\n',
        java: '// Write your solution here\npublic class Solution {\n    public void solution() {\n        \n    }\n}\n'
      };
      setCode(templates[language]);

    } catch (error) {
      console.error('Failed to start interview:', error);
      
      // Handle credit-related errors
      if (error.response?.status === 402) {
        // No credits available
        setShowPaymentModal(true);
        return;
      } else if (error.response?.status === 401) {
        // Authentication required
        setShowLoginPrompt(true);
        return;
      }
      
      alert(error.response?.data?.error || 'Failed to start interview. Please try again.');
    } finally {
      setIsStartingInterview(false);
    }
  };

  const sendMessage = async () => {
    if (!currentMessage.trim()) return;

    const userMessage = {
      role: 'candidate',
      content: currentMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setCurrentMessage('');
    setIsSending(true);
    setLastInteractionTime(Date.now()); // Reset interaction timer

    try {
      const token = localStorage.getItem('token');
      const conversationHistory = messages
        .map(m => `${m.role === 'interviewer' ? 'Interviewer' : 'Candidate'}: ${m.content}`)
        .join('\n');

      const response = await api.post(
        '/api/ai-assistant/interview/chat',
        {
          message: currentMessage,
          conversationHistory: conversationHistory,
          problemContext: problem
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const interviewerMessage = {
        role: 'interviewer',
        content: response.data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, interviewerMessage]);
      setLastInteractionTime(Date.now()); // Reset after interviewer responds
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsSending(false);
    }
  };

  // Proactive interviewer check-in
  const sendInterviewerCheckIn = useCallback(async () => {
    if (isSending || !interviewStarted || showEvaluation) return;

    setIsSending(true);
    
    try {
      const token = localStorage.getItem('token');
      const conversationHistory = messages
        .map(m => `${m.role === 'interviewer' ? 'Interviewer' : 'Candidate'}: ${m.content}`)
        .join('\n');

      // Send a special message to trigger proactive check-in
      const response = await api.post(
        '/api/ai-assistant/interview/chat',
        {
          message: "[INTERVIEWER: The candidate has been coding silently. Check in on their progress and ask them to explain their approach so far]",
          conversationHistory: conversationHistory,
          problemContext: problem
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const interviewerMessage = {
        role: 'interviewer',
        content: response.data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, interviewerMessage]);
      setLastInteractionTime(Date.now());
    } catch (error) {
      console.error('Failed to send check-in:', error);
    } finally {
      setIsSending(false);
    }
  }, [isSending, interviewStarted, showEvaluation, messages, problem]);

  // Auto check-in timer
  useEffect(() => {
    if (!interviewStarted || showEvaluation) return;

    const checkInInterval = setInterval(() => {
      const timeSinceLastInteraction = Date.now() - lastInteractionTime;
      // Check in every 90 seconds of silence
      if (timeSinceLastInteraction > 90000 && messages.length > 1) {
        sendInterviewerCheckIn();
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(checkInInterval);
  }, [interviewStarted, showEvaluation, lastInteractionTime, messages.length, sendInterviewerCheckIn]);

  // Warn user before leaving during active interview
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Only show warning if interview is in progress (not in setup or evaluation)
      if (interviewStarted && !showEvaluation) {
        e.preventDefault();
        e.returnValue = ''; // Modern browsers require this
        return ''; // Some older browsers need a return value
      }
    };

    // Handle browser back button
    const handlePopState = (e) => {
      if (interviewStarted && !showEvaluation) {
        e.preventDefault();
        setShowNavigationWarning(true);
        setPendingNavigation({ type: 'back' });
        // Push state back to prevent immediate navigation
        window.history.pushState(null, '', window.location.pathname);
      }
    };

    // Handle navbar and internal link clicks
    const handleLinkClick = (e) => {
      if (!interviewStarted || showEvaluation) return;

      // Check if the clicked element is a link or inside a link
      let target = e.target;
      while (target && target !== document.body) {
        // Check if this is a navigation element (navbar link, button, etc.)
        const isInNavbar = target.closest('nav') !== null;
        const isLink = target.tagName === 'A';
        const href = target.getAttribute('href');
        
        // If it's in navbar or a link with href starting with /
        if (isInNavbar || (isLink && href && href.startsWith('/'))) {
          // Don't block internal interview actions
          const isInternalAction = target.closest('[data-interview-action]') !== null;
          
          if (!isInternalAction) {
            e.preventDefault();
            e.stopPropagation();
            setShowNavigationWarning(true);
            setPendingNavigation({ type: 'link', element: target, href: href });
            return false;
          }
        }
        target = target.parentElement;
      }
    };

    // Add popstate listener and push initial state
    if (interviewStarted && !showEvaluation) {
      window.history.pushState(null, '', window.location.pathname);
      window.addEventListener('popstate', handlePopState);
      document.addEventListener('click', handleLinkClick, true); // Use capture phase
    }

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('click', handleLinkClick, true);
    };
  }, [interviewStarted, showEvaluation]);

  const submitAndEvaluate = async () => {
    if (!code.trim()) {
      alert('Please write some code before submitting!');
      return;
    }

    setIsEvaluating(true);

    try {
      const token = localStorage.getItem('token');
      const conversationHistory = messages
        .map(m => `${m.role === 'interviewer' ? 'Interviewer' : 'Candidate'}: ${m.content}`)
        .join('\n');

      // Get evaluation
      const response = await api.post(
        '/api/ai-assistant/interview/evaluate',
        {
          code: code,
          language: language,
          problemContext: problem,
          conversationHistory: conversationHistory
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const evaluationText = response.data.evaluation;
      setEvaluation(evaluationText);
      setShowEvaluation(true);
      
      // Show upsell modal if this was a free interview
      if (usedFreeInterview) {
        // Show upsell modal after a short delay
        setTimeout(() => {
          setShowUpsellModal(true);
        }, 2000);
      }

      // Save session to database
      try {
        await api.post(
          '/api/ai-assistant/interview/save',
          {
            difficulty: difficulty,
            focusArea: focus,
            language: language,
            problemStatement: problem,
            finalCode: code,
            evaluation: evaluationText,
            conversationHistory: conversationHistory,
            startTime: startTime
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('✅ Interview session saved successfully');
      } catch (saveError) {
        console.error('⚠️ Failed to save interview session:', saveError);
        // Don't show error to user, just log it
      }

    } catch (error) {
      console.error('Failed to evaluate interview:', error);
      alert('Failed to evaluate interview. Please try again.');
    } finally {
      setIsEvaluating(false);
    }
  };

  const resetInterview = () => {
    setInterviewStarted(false);
    setSessionId(null);
    setProblem('');
    setMessages([]);
    setCode('');
    setShowEvaluation(false);
    setEvaluation(null);
    setLastInteractionTime(Date.now());
  };


  // Helper function to render problem statement with code syntax highlighting
  const renderProblemContent = (problemText) => {
    if (!problemText) return null;

    const lines = problemText.split('\n');
    const elements = [];
    let codeBlock = [];
    // eslint-disable-next-line no-unused-vars
    let _codeLanguage = ''; // Extracted but not currently used
    let inCodeBlock = false;

    lines.forEach((line, idx) => {
      // Detect code block start
      if (line.startsWith('```')) {
        if (inCodeBlock) {
          // End of code block
          elements.push(
            <pre key={`code-${idx}`} style={{
              background: '#282c34',
              color: '#abb2bf',
              padding: '16px',
              borderRadius: '8px',
              overflow: 'auto',
              fontSize: '13px',
              lineHeight: '1.6',
              fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
              margin: '12px 0',
              border: '1px solid #3e4451'
            }}>
              <code>{codeBlock.join('\n')}</code>
            </pre>
          );
          codeBlock = [];
          inCodeBlock = false;
          _codeLanguage = '';
        } else {
          // Start of code block
          _codeLanguage = line.replace('```', '').trim();
          inCodeBlock = true;
        }
        return;
      }

      if (inCodeBlock) {
        codeBlock.push(line);
        return;
      }

      // Render headings
      if (line.startsWith('## ')) {
        elements.push(
          <h2 key={`h2-${idx}`} style={{ 
            fontSize: '16px', 
            fontWeight: '700', 
            color: '#1f2937', 
            marginTop: '16px', 
            marginBottom: '8px' 
          }}>
            {line.replace('## ', '')}
          </h2>
        );
        return;
      }

      if (line.startsWith('# ')) {
        elements.push(
          <h1 key={`h1-${idx}`} style={{ 
            fontSize: '18px', 
            fontWeight: '700', 
            color: '#1f2937', 
            marginTop: '16px', 
            marginBottom: '8px' 
          }}>
            {line.replace('# ', '')}
          </h1>
        );
        return;
      }

      // Render bold and inline code
      const parts = line.split(/(\*\*.*?\*\*|`.*?`)/g);
      elements.push(
        <p key={`p-${idx}`} style={{ margin: '8px 0', lineHeight: '1.7' }}>
          {parts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={i} style={{ color: '#1f2937' }}>{part.slice(2, -2)}</strong>;
            }
            if (part.startsWith('`') && part.endsWith('`')) {
              return (
                <code key={i} style={{
                  background: '#f3f4f6',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
                  color: '#dc2626'
                }}>
                  {part.slice(1, -1)}
                </code>
              );
            }
            return part;
          })}
        </p>
      );
    });

    return <div>{elements}</div>;
  };

  return (
    <>
      <Navbar />
      
      {/* Starting Interview Loading Overlay */}
      {isStartingInterview && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          backdropFilter: 'blur(8px)'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '50px 60px',
            textAlign: 'center',
            boxShadow: '0 25px 80px rgba(0,0,0,0.4)',
            maxWidth: '450px',
            animation: 'fadeIn 0.3s ease-out'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              border: '5px solid #e5e7eb',
              borderTop: '5px solid #2563eb',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 25px'
            }} />
            <h3 style={{
              margin: '0 0 15px 0',
              fontSize: '24px',
              fontWeight: '700',
              color: '#1f2937',
              letterSpacing: '-0.5px'
            }}>
              🎤 Starting Your Mock Interview
            </h3>
            <p style={{
              margin: 0,
              fontSize: '15px',
              color: '#6b7280',
              lineHeight: '1.7'
            }}>
              Your AI coach is preparing a <strong style={{ color: '#2563eb' }}>{difficulty.toLowerCase()}</strong> level problem on <strong style={{ color: '#2563eb' }}>Data Structures and Algorithms</strong>.
              <br /><br />
              This may take 10-20 seconds...
            </p>
            <div style={{
              marginTop: '25px',
              display: 'flex',
              justifyContent: 'center',
              gap: '8px'
            }}>
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  style={{
                    width: '10px',
                    height: '10px',
                    background: '#2563eb',
                    borderRadius: '50%',
                    animation: `pulse 1.6s ease-in-out ${i * 0.2}s infinite`
                  }}
                />
              ))}
            </div>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              @keyframes pulse {
                0%, 100% { opacity: 0.2; transform: scale(0.7); }
                50% { opacity: 1; transform: scale(1.3); }
              }
              @keyframes fadeIn {
                from { opacity: 0; transform: scale(0.95); }
                to { opacity: 1; transform: scale(1); }
              }
            `}</style>
          </div>
        </div>
      )}
      
      <div style={{ 
        paddingTop: '64px', 
        minHeight: '100vh',
        background: 'linear-gradient(to bottom right, #111827, #1f2937, #111827)'
      }}>
        {!interviewStarted ? (
        // Setup Screen
        <div style={{ 
          maxWidth: '900px', 
          margin: '0 auto', 
          padding: isMobile ? '20px 16px' : '40px 20px',
          minHeight: 'calc(100vh - 64px)'
        }}>
          {/* Compact Header */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.2) 0%, rgba(59, 130, 246, 0.2) 50%, rgba(139, 92, 246, 0.2) 100%)',
            backdropFilter: 'blur(10px)',
            padding: isMobile ? '20px 16px' : '24px 32px',
            borderRadius: isMobile ? '12px' : '16px',
            marginBottom: isMobile ? '20px' : '28px',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: 'space-between',
            gap: isMobile ? '12px' : '20px'
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <span style={{ fontSize: isMobile ? '28px' : '32px' }}>🎤</span>
                <h1 style={{ 
                  margin: 0, 
                  color: 'white', 
                  fontSize: isMobile ? '22px' : '28px', 
                  fontWeight: '700',
                  letterSpacing: '-0.3px'
                }}>
                  {isMobile ? 'AI Mock Interviews' : 'AI Mock Interviews'}
                </h1>
              </div>
              <p style={{ 
                margin: 0, 
                color: 'rgba(255,255,255,0.85)', 
                fontSize: isMobile ? '13px' : '15px',
                fontWeight: '400',
                paddingLeft: isMobile ? '0' : '44px'
              }}>
                Master technical interviews with AI-powered real-time coaching
              </p>
            </div>
            <div style={{
              display: 'flex',
              gap: isMobile ? '6px' : '10px',
              flexWrap: 'wrap',
              justifyContent: isMobile ? 'flex-start' : 'flex-end'
            }}>
              {['✨ Realistic', '🎯 Feedback', '📈 Progress'].map((feature, i) => (
                <span key={i} style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  padding: isMobile ? '4px 10px' : '6px 12px',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: isMobile ? '10px' : '11px',
                  fontWeight: '600',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  whiteSpace: 'nowrap'
                }}>
                  {feature}
                </span>
              ))}
            </div>
          </div>

          {/* Setup Form */}
          <div style={{
            background: 'white',
            padding: isMobile ? '24px 16px' : '40px',
            borderRadius: isMobile ? '16px' : '20px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            border: '1px solid #f0f0f0',
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '10px' : '12px',
              marginBottom: isMobile ? '24px' : '35px'
            }}>
              <div style={{
                width: isMobile ? '40px' : '50px',
                height: isMobile ? '40px' : '50px',
                background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                borderRadius: isMobile ? '10px' : '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isMobile ? '20px' : '24px'
              }}>⚙️</div>
              <h2 style={{ margin: 0, color: '#1f2937', fontSize: isMobile ? '20px' : '28px', fontWeight: '700' }}>
                Configure Your Interview
              </h2>
            </div>

            {/* Difficulty */}
            <div style={{ marginBottom: isMobile ? '28px' : '35px', position: 'relative' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: isMobile ? '16px' : '15px',
                fontWeight: '700',
                color: '#374151',
                fontSize: isMobile ? '16px' : '16px'
              }}>
                <span style={{ fontSize: isMobile ? '20px' : '20px' }}>🎚️</span>
                Interview Level
              </label>
              
              {isMobile ? (
                // Mobile: Dropdown
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => {
                      setShowDifficultyDropdown(!showDifficultyDropdown);
                      setShowLanguageDropdown(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '16px 14px',
                      border: `3px solid ${(() => {
                        const colors = { 'EASY': '#10b981', 'MEDIUM': '#f59e0b', 'HARD': '#ef4444', 'EXPERT': '#8b5cf6' };
                        return colors[difficulty] || '#e5e7eb';
                      })()}`,
                      borderRadius: '12px',
                      background: (() => {
                        const colors = { 'EASY': '#10b98115', 'MEDIUM': '#f59e0b15', 'HARD': '#ef444415', 'EXPERT': '#8b5cf615' };
                        return colors[difficulty] || 'white';
                      })(),
                      cursor: 'pointer',
                      fontWeight: '700',
                      color: (() => {
                        const colors = { 'EASY': '#10b981', 'MEDIUM': '#f59e0b', 'HARD': '#ef4444', 'EXPERT': '#8b5cf6' };
                        return colors[difficulty] || '#6b7280';
                      })(),
                      fontSize: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      minHeight: '56px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '24px' }}>
                        {(() => {
                          const emojis = { 'EASY': '🟢', 'MEDIUM': '🟡', 'HARD': '🔴', 'EXPERT': '⭐' };
                          return emojis[difficulty] || '🟢';
                        })()}
                      </span>
                      <span>{difficulty}</span>
                    </div>
                    <span style={{ fontSize: '20px', transition: 'transform 0.2s', transform: showDifficultyDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                      ▼
                    </span>
                  </button>
                  
                  {showDifficultyDropdown && (
                    <>
                      <div
                        style={{
                          position: 'fixed',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          zIndex: 100,
                          background: 'transparent',
                          touchAction: 'none'
                        }}
                        onClick={() => setShowDifficultyDropdown(false)}
                        onTouchStart={(e) => {
                          e.preventDefault();
                          setShowDifficultyDropdown(false);
                        }}
                      />
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        marginTop: '8px',
                        background: 'white',
                        borderRadius: '12px',
                        border: '2px solid #e5e7eb',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                        zIndex: 101,
                        overflow: 'hidden'
                      }}>
                        {[
                          { level: 'EASY', color: '#10b981', emoji: '🟢' },
                          { level: 'MEDIUM', color: '#f59e0b', emoji: '🟡' },
                          { level: 'HARD', color: '#ef4444', emoji: '🔴' },
                          { level: 'EXPERT', color: '#8b5cf6', emoji: '⭐' }
                        ].map(({ level, color, emoji }) => (
                          <button
                            key={level}
                            onClick={() => {
                              setDifficulty(level);
                              setShowDifficultyDropdown(false);
                            }}
                            style={{
                              width: '100%',
                              padding: '16px 14px',
                              border: 'none',
                              borderBottom: level !== 'EXPERT' ? '1px solid #f3f4f6' : 'none',
                              background: difficulty === level ? `${color}15` : 'white',
                              cursor: 'pointer',
                              fontWeight: '700',
                              color: difficulty === level ? color : '#6b7280',
                              fontSize: '15px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              transition: 'all 0.2s',
                              minHeight: '56px'
                            }}
                          >
                            <span style={{ fontSize: '24px' }}>{emoji}</span>
                            <span>{level}</span>
                            {difficulty === level && (
                              <span style={{ marginLeft: 'auto', fontSize: '18px', color: color }}>✓</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                // Desktop: Button Grid
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                  {[
                    { level: 'EASY', color: '#10b981', emoji: '🟢' },
                    { level: 'MEDIUM', color: '#f59e0b', emoji: '🟡' },
                    { level: 'HARD', color: '#ef4444', emoji: '🔴' },
                    { level: 'EXPERT', color: '#8b5cf6', emoji: '⭐' }
                  ].map(({ level, color, emoji }) => (
                    <button
                      key={level}
                      onClick={() => setDifficulty(level)}
                      style={{
                        padding: '16px 12px',
                        border: difficulty === level ? `3px solid ${color}` : '2px solid #e5e7eb',
                        borderRadius: '12px',
                        background: difficulty === level ? `${color}15` : 'white',
                        cursor: 'pointer',
                        fontWeight: '700',
                        color: difficulty === level ? color : '#6b7280',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        fontSize: '15px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        transform: difficulty === level ? 'scale(1.05)' : 'scale(1)',
                        boxShadow: difficulty === level ? `0 4px 12px ${color}40` : '0 2px 4px rgba(0,0,0,0.05)'
                      }}
                      onMouseEnter={(e) => {
                        if (difficulty !== level) {
                          e.currentTarget.style.borderColor = '#d1d5db';
                          e.currentTarget.style.transform = 'scale(1.02)';
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (difficulty !== level) {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                        }
                      }}
                    >
                      <span style={{ fontSize: '24px', lineHeight: '1' }}>{emoji}</span>
                      <span style={{ fontSize: '15px', fontWeight: '700', letterSpacing: '0.3px' }}>{level}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Focus Area - Fixed to DSA */}
            <div style={{ marginBottom: isMobile ? '24px' : '35px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: isMobile ? '12px' : '15px',
                fontWeight: '700',
                color: '#374151',
                fontSize: isMobile ? '14px' : '16px'
              }}>
                <span style={{ fontSize: isMobile ? '18px' : '20px' }}>🎯</span>
                Focus Area
              </label>
              <div style={{
                width: '100%',
                padding: isMobile ? '12px' : '16px',
                borderRadius: isMobile ? '10px' : '12px',
                border: '2px solid #e5e7eb',
                fontSize: isMobile ? '13px' : '15px',
                background: '#f9fafb',
                fontWeight: '600',
                color: '#374151',
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? '8px' : '10px'
              }}>
                <span style={{ 
                  fontSize: isMobile ? '18px' : '20px',
                  background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                  width: isMobile ? '28px' : '32px',
                  height: isMobile ? '28px' : '32px',
                  borderRadius: isMobile ? '6px' : '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>📊</span>
                <span>{isMobile ? 'Data Structures & Algorithms' : 'Data Structures and Algorithms'}</span>
              </div>
            </div>

            {/* Language */}
            <div style={{ marginBottom: isMobile ? '28px' : '35px', position: 'relative' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: isMobile ? '16px' : '15px',
                fontWeight: '700',
                color: '#374151',
                fontSize: isMobile ? '16px' : '16px'
              }}>
                <span style={{ fontSize: isMobile ? '20px' : '20px' }}>💻</span>
                Programming Language
              </label>
              
              {isMobile ? (
                // Mobile: Dropdown
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => {
                      setShowLanguageDropdown(!showLanguageDropdown);
                      setShowDifficultyDropdown(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '16px 14px',
                      border: language === 'javascript' || language === 'python' || language === 'java' 
                        ? '3px solid #3b82f6' 
                        : '2px solid #e5e7eb',
                      borderRadius: '12px',
                      background: language === 'javascript' || language === 'python' || language === 'java' 
                        ? '#eff6ff' 
                        : 'white',
                      cursor: 'pointer',
                      fontWeight: '700',
                      color: language === 'javascript' || language === 'python' || language === 'java' 
                        ? '#2563eb' 
                        : '#6b7280',
                      fontSize: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      minHeight: '56px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '28px' }}>
                        {(() => {
                          const emojis = { 'javascript': '📜', 'python': '🐍', 'java': '☕' };
                          return emojis[language] || '📜';
                        })()}
                      </span>
                      <span>
                        {(() => {
                          const labels = { 'javascript': 'JavaScript', 'python': 'Python', 'java': 'Java' };
                          return labels[language] || 'JavaScript';
                        })()}
                      </span>
                    </div>
                    <span style={{ fontSize: '20px', transition: 'transform 0.2s', transform: showLanguageDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                      ▼
                    </span>
                  </button>
                  
                  {showLanguageDropdown && (
                    <>
                      <div
                        style={{
                          position: 'fixed',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          zIndex: 100,
                          background: 'transparent',
                          touchAction: 'none'
                        }}
                        onClick={() => setShowLanguageDropdown(false)}
                        onTouchStart={(e) => {
                          e.preventDefault();
                          setShowLanguageDropdown(false);
                        }}
                      />
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        marginTop: '8px',
                        background: 'white',
                        borderRadius: '12px',
                        border: '2px solid #e5e7eb',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                        zIndex: 101,
                        overflow: 'hidden'
                      }}>
                        {[
                          { value: 'javascript', label: 'JavaScript', emoji: '📜' },
                          { value: 'python', label: 'Python', emoji: '🐍' },
                          { value: 'java', label: 'Java', emoji: '☕' }
                        ].map(lang => (
                          <button
                            key={lang.value}
                            onClick={() => {
                              setLanguage(lang.value);
                              setShowLanguageDropdown(false);
                            }}
                            style={{
                              width: '100%',
                              padding: '16px 14px',
                              border: 'none',
                              borderBottom: lang.value !== 'java' ? '1px solid #f3f4f6' : 'none',
                              background: language === lang.value ? '#eff6ff' : 'white',
                              cursor: 'pointer',
                              fontWeight: '700',
                              color: language === lang.value ? '#2563eb' : '#6b7280',
                              fontSize: '15px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              transition: 'all 0.2s',
                              minHeight: '56px'
                            }}
                          >
                            <span style={{ fontSize: '28px' }}>{lang.emoji}</span>
                            <span>{lang.label}</span>
                            {language === lang.value && (
                              <span style={{ marginLeft: 'auto', fontSize: '18px', color: '#2563eb' }}>✓</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                // Desktop: Button Grid
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  {[
                    { value: 'javascript', label: 'JavaScript', emoji: '📜', color: '#f7df1e' },
                    { value: 'python', label: 'Python', emoji: '🐍', color: '#3776ab' },
                    { value: 'java', label: 'Java', emoji: '☕', color: '#f89820' }
                  ].map(lang => (
                    <button
                      key={lang.value}
                      onClick={() => setLanguage(lang.value)}
                      style={{
                        padding: '16px 12px',
                        border: language === lang.value ? '3px solid #3b82f6' : '2px solid #e5e7eb',
                        borderRadius: '12px',
                        background: language === lang.value ? '#eff6ff' : 'white',
                        cursor: 'pointer',
                        fontWeight: '700',
                        color: language === lang.value ? '#2563eb' : '#6b7280',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        fontSize: '15px',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transform: language === lang.value ? 'scale(1.02)' : 'scale(1)',
                        boxShadow: language === lang.value ? '0 4px 12px rgba(59, 130, 246, 0.3)' : '0 2px 4px rgba(0,0,0,0.05)'
                      }}
                      onMouseEnter={(e) => {
                        if (language !== lang.value) {
                          e.currentTarget.style.borderColor = '#d1d5db';
                          e.currentTarget.style.transform = 'scale(1.02)';
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (language !== lang.value) {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                        }
                      }}
                    >
                      <span style={{ fontSize: '32px', flexShrink: 0, lineHeight: '1' }}>{lang.emoji}</span>
                      <span style={{ fontSize: '15px', fontWeight: '700' }}>{lang.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Credit Status */}
            {isAuthenticated() && interviewCredits && (
              <div style={{
                marginBottom: '16px',
                padding: '12px',
                background: interviewCredits.hasAvailableCredits 
                  ? 'linear-gradient(135deg, #ecfdf5, #d1fae5)' 
                  : 'linear-gradient(135deg, #fef3c7, #fde68a)',
                borderRadius: '12px',
                border: `1px solid ${interviewCredits.hasAvailableCredits ? '#86efac' : '#fcd34d'}`,
                fontSize: isMobile ? '13px' : '14px'
              }}>
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 font-medium">
                    {interviewCredits.remainingFreeInterviews > 0 
                      ? `✓ Free: ${interviewCredits.remainingFreeInterviews} interview${interviewCredits.remainingFreeInterviews > 1 ? 's' : ''} available`
                      : interviewCredits.paidCredits > 0
                      ? `✓ ${interviewCredits.paidCredits} paid interview${interviewCredits.paidCredits > 1 ? 's' : ''} available`
                      : 'No interviews available'}
                  </span>
                  {!interviewCredits.hasAvailableCredits && (
                    <button
                      onClick={() => setShowPaymentModal(true)}
                      className="text-purple-600 hover:text-purple-700 font-semibold text-xs underline"
                    >
                      Get More
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Start Button */}
            <button
              data-interview-action="true"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                startInterview();
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
              }}
              disabled={isStartingInterview || (isAuthenticated() && !interviewCredits?.hasAvailableCredits)}
              style={{
                width: '100%',
                padding: isMobile ? '16px' : '20px',
                background: (isStartingInterview || (isAuthenticated() && !interviewCredits?.hasAvailableCredits))
                  ? 'linear-gradient(135deg, #9ca3af, #6b7280)' 
                  : 'linear-gradient(135deg, #2563eb, #3b82f6)',
                color: 'white',
                border: 'none',
                borderRadius: isMobile ? '12px' : '14px',
                fontSize: isMobile ? '16px' : '20px',
                fontWeight: '700',
                cursor: (isStartingInterview || (isAuthenticated() && !interviewCredits?.hasAvailableCredits)) ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: (isStartingInterview || (isAuthenticated() && !interviewCredits?.hasAvailableCredits))
                  ? '0 4px 12px rgba(0,0,0,0.1)' 
                  : '0 8px 24px rgba(37, 99, 235, 0.4)',
                opacity: (isStartingInterview || (isAuthenticated() && !interviewCredits?.hasAvailableCredits)) ? 0.7 : 1,
                position: 'relative',
                overflow: 'hidden',
                minHeight: isMobile ? '52px' : 'auto',
                zIndex: 10,
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'manipulation'
              }}
              onMouseEnter={(e) => {
                if (!isStartingInterview && (isAuthenticated() ? interviewCredits?.hasAvailableCredits : true)) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 12px 32px rgba(37, 99, 235, 0.5)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isStartingInterview && (isAuthenticated() ? interviewCredits?.hasAvailableCredits : true)) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 8px 24px rgba(37, 99, 235, 0.4)';
                }
              }}
            >
              {isStartingInterview 
                ? '⏳ Starting Interview...' 
                : (isAuthenticated() && !interviewCredits?.hasAvailableCredits)
                ? '💳 Get Credits to Start'
                : (isMobile ? '🎤 Start Interview' : '🎤 Start Mock Interview')}
            </button>

            {/* Info */}
            <div style={{
              marginTop: isMobile ? '24px' : '30px',
              padding: isMobile ? '16px' : '24px',
              background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
              borderRadius: isMobile ? '12px' : '16px',
              border: '1px solid #93c5fd'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? '8px' : '10px',
                marginBottom: isMobile ? '12px' : '16px'
              }}>
                <div style={{
                  width: isMobile ? '32px' : '40px',
                  height: isMobile ? '32px' : '40px',
                  background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                  borderRadius: isMobile ? '8px' : '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isMobile ? '16px' : '20px'
                }}>💡</div>
                <h4 style={{ margin: 0, fontSize: isMobile ? '15px' : '17px', color: '#1e40af', fontWeight: '700' }}>
                  What to Expect
                </h4>
              </div>
              <ul style={{ 
                margin: 0, 
                paddingLeft: isMobile ? '20px' : '20px', 
                fontSize: isMobile ? '13px' : '14px', 
                color: '#1e3a8a', 
                lineHeight: '1.9',
                fontWeight: '500'
              }}>
                <li>AI interviewer will present a coding problem</li>
                <li>Ask clarifying questions just like in a real interview</li>
                <li>Explain your approach as you code</li>
                <li>Get comprehensive feedback on your performance</li>
              </ul>
            </div>
          </div>
        </div>
      ) : (
        // Interview Screen
        <div>
          {!showEvaluation ? (
            <>
              {/* Fixed Header Bar */}
              <div style={{
                position: 'fixed',
                top: isMobile ? '60px' : '80px',
                left: 0,
                right: 0,
                background: '#1a1a2e',
                padding: isMobile ? '12px 16px' : '16px 30px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'flex-start' : 'center',
                gap: isMobile ? '12px' : '0',
                zIndex: 50,
                borderBottom: '1px solid #2d2d44',
                minHeight: isMobile ? 'auto' : 'auto'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '15px', flex: isMobile ? 1 : 'auto' }}>
                  <span style={{ fontSize: isMobile ? '20px' : '24px' }}>🎤</span>
                  <div>
                    <h2 style={{ margin: 0, color: '#ffffff', fontSize: isMobile ? '16px' : '18px', fontWeight: '600' }}>
                      {isMobile ? 'Interview' : 'Mock Interview in Progress'}
                    </h2>
                    <p style={{ margin: '2px 0 0 0', color: '#a0a0b0', fontSize: isMobile ? '11px' : '12px' }}>
                      {difficulty} • DSA
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: isMobile ? '8px' : '12px', width: isMobile ? '100%' : 'auto' }}>
                  <button
                    data-interview-action="true"
                    onClick={submitAndEvaluate}
                    disabled={isEvaluating}
                    style={{
                      padding: isMobile ? '12px 16px' : '10px 24px',
                      background: isEvaluating ? '#5a5a6e' : '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: isEvaluating ? 'not-allowed' : 'pointer',
                      fontSize: isMobile ? '14px' : '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      transition: 'all 0.2s',
                      flex: isMobile ? 1 : 'auto',
                      minHeight: isMobile ? '44px' : 'auto'
                    }}
                    onMouseEnter={(e) => {
                      if (!isEvaluating) e.target.style.background = '#059669';
                    }}
                    onMouseLeave={(e) => {
                      if (!isEvaluating) e.target.style.background = '#10b981';
                    }}
                  >
                    {isEvaluating ? (
                      <>
                        <div style={{
                          width: '14px',
                          height: '14px',
                          border: '2px solid rgba(255,255,255,0.3)',
                          borderTop: '2px solid white',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                        {isMobile ? 'Evaluating...' : 'Evaluating...'}
                      </>
                    ) : (
                      <>{isMobile ? '✓ Submit' : '✓ Submit & Get Feedback'}</>
                    )}
                  </button>
                  <button
                    onClick={resetInterview}
                    style={{
                      padding: isMobile ? '12px 16px' : '10px 20px',
                      background: 'transparent',
                      color: '#e0e0e8',
                      border: '1px solid #3d3d54',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: isMobile ? '14px' : '14px',
                      transition: 'all 0.2s',
                      minHeight: isMobile ? '44px' : 'auto',
                      minWidth: isMobile ? '44px' : 'auto'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#2d2d44';
                      e.target.style.borderColor = '#4d4d64';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'transparent';
                      e.target.style.borderColor = '#3d3d54';
                    }}
                  >
                    {isMobile ? '×' : '× End Interview'}
                  </button>
                </div>
              </div>

              {/* Main Content */}
              <div style={{ 
                position: 'fixed',
                top: isMobile ? '200px' : '144px',
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                background: '#f8f9fa',
                overflow: isMobile ? 'hidden' : 'hidden',
                zIndex: 30
              }}>
                {/* Mobile Tab Navigation - Fixed at top */}
                {isMobile && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    background: '#ffffff',
                    borderBottom: '2px solid #e5e7eb',
                    display: 'flex',
                    zIndex: 100,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    flexShrink: 0,
                    width: '100%'
                  }}>
                    {[
                      { id: 'problem', label: 'Problem', icon: '📋' },
                      { id: 'chat', label: 'Chat', icon: '💬' },
                      { id: 'editor', label: 'Code', icon: '💻' }
                    ].map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveMobileTab(tab.id)}
                        style={{
                          flex: 1,
                          padding: '14px 8px',
                          background: activeMobileTab === tab.id ? '#2563eb' : 'transparent',
                          color: activeMobileTab === tab.id ? 'white' : '#6b7280',
                          border: 'none',
                          borderBottom: activeMobileTab === tab.id ? '3px solid #1d4ed8' : '3px solid transparent',
                          fontWeight: '700',
                          fontSize: '13px',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          minHeight: '48px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          touchAction: 'manipulation',
                          WebkitTapHighlightColor: 'transparent'
                        }}
                      >
                        <span style={{ fontSize: '18px' }}>{tab.icon}</span>
                        <span>{tab.label}</span>
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Panel 1: Problem Statement */}
                {(!isMobile || activeMobileTab === 'problem') && (
                  <div style={{ 
                    width: isMobile ? '100%' : `${problemWidth}%`,
                    display: 'flex', 
                    flexDirection: 'column',
                    borderRight: isMobile ? 'none' : '1px solid #dde1e6',
                    background: 'white',
                    height: isMobile ? 'calc(100% - 48px)' : '100%',
                    overflow: 'hidden',
                    flexShrink: 0,
                    marginTop: isMobile ? '48px' : 0
                  }}>
                  <div style={{
                    padding: isMobile ? '16px' : '20px',
                    height: '100%',
                    overflowY: 'auto',
                    background: '#ffffff',
                    WebkitOverflowScrolling: 'touch',
                    willChange: 'scroll-position',
                    transform: 'translateZ(0)'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: isMobile ? '12px' : '12px',
                      paddingBottom: isMobile ? '10px' : '8px',
                      borderBottom: '2px solid #e8eaed'
                    }}>
                      <span style={{ fontSize: isMobile ? '20px' : '18px', marginRight: '10px' }}>📋</span>
                      <h3 style={{ margin: 0, color: '#1f2937', fontSize: isMobile ? '16px' : '15px', fontWeight: '700' }}>
                        Problem Statement
                      </h3>
                    </div>
                    <div style={{ 
                      fontSize: isMobile ? '14px' : '13px', 
                      color: '#4b5563', 
                      lineHeight: '1.8',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      textAlign: 'left',
                      wordBreak: 'break-word'
                    }}>
                      {renderProblemContent(problem)}
                    </div>
                  </div>
                </div>
                )}

                {/* Resize Handle 1: Problem <-> Chat - Hidden on mobile */}
                {!isMobile && (
                  <div
                    onMouseDown={() => setIsResizingProblem(true)}
                    style={{
                      width: '8px',
                      background: '#e5e7eb',
                      cursor: 'col-resize',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderLeft: '1px solid #d1d5db',
                      borderRight: '1px solid #d1d5db',
                      transition: 'background 0.2s',
                      flexShrink: 0
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#cbd5e1'}
                    onMouseLeave={(e) => e.target.style.background = '#e5e7eb'}
                  >
                    <div style={{
                      width: '3px',
                      height: '40px',
                      background: '#94a3b8',
                      borderRadius: '2px'
                    }} />
                  </div>
                )}

                {/* Panel 2: Chat Interface */}
                {(!isMobile || activeMobileTab === 'chat') && (
                  <div style={{
                    width: isMobile ? '100%' : `${chatWidth}%`,
                    display: 'flex',
                    flexDirection: 'column',
                    background: 'white',
                    height: isMobile ? 'calc(100% - 48px)' : '100%',
                    overflow: 'hidden',
                    flexShrink: 0,
                    marginTop: isMobile ? '48px' : 0
                  }}>
                    <div style={{
                      padding: isMobile ? '14px 16px' : '14px 20px',
                      borderBottom: '1px solid #dde1e6',
                      background: '#ffffff',
                      flexShrink: 0
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontSize: isMobile ? '20px' : '18px', marginRight: '10px' }}>💬</span>
                        <h3 style={{ margin: 0, color: '#1f2937', fontSize: isMobile ? '16px' : '15px', fontWeight: '700' }}>
                          Interview Chat
                        </h3>
                      </div>
                      {!isMobile && (
                        <p style={{ margin: '4px 0 0 30px', fontSize: '11px', color: '#6b7280' }}>
                          Ask clarifying questions & explain your approach
                        </p>
                      )}
                    </div>

                    {/* Messages */}
                    <div style={{ 
                      flex: 1, 
                      overflowY: 'auto', 
                      padding: isMobile ? '16px' : '20px',
                      background: '#f9fafb',
                      WebkitOverflowScrolling: 'touch',
                      willChange: 'scroll-position',
                      transform: 'translateZ(0)'
                    }}>
                      {messages.slice(1).map((msg, idx) => (
                        <div
                          key={idx}
                          style={{
                            marginBottom: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: msg.role === 'candidate' ? 'flex-end' : 'flex-start'
                          }}
                        >
                          <div style={{
                            maxWidth: isMobile ? '85%' : '85%',
                            padding: isMobile ? '12px 14px' : '12px 14px',
                            borderRadius: msg.role === 'candidate' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                            background: msg.role === 'candidate' 
                              ? '#2563eb' 
                              : '#ffffff',
                            color: msg.role === 'candidate' ? '#ffffff' : '#1f2937',
                            fontSize: isMobile ? '14px' : '13px',
                            lineHeight: '1.6',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            boxShadow: msg.role === 'candidate' 
                              ? '0 2px 6px rgba(37, 99, 235, 0.3)' 
                              : '0 2px 6px rgba(0,0,0,0.1)',
                            border: msg.role === 'candidate' ? 'none' : '1px solid #e5e7eb',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                          }}>
                            <div style={{ 
                              fontWeight: '700', 
                              marginBottom: '8px', 
                              fontSize: isMobile ? '11px' : '10px', 
                              opacity: msg.role === 'candidate' ? 0.95 : 0.7,
                              letterSpacing: '0.3px',
                              textTransform: 'uppercase'
                            }}>
                              {msg.role === 'candidate' ? 'You' : 'AI Interviewer'}
                            </div>
                            {msg.content}
                          </div>
                          <div style={{ 
                            fontSize: '10px', 
                            color: '#9ca3af', 
                            marginTop: '4px',
                            marginLeft: msg.role === 'candidate' ? 0 : '12px',
                            marginRight: msg.role === 'candidate' ? '12px' : 0
                          }}>
                            {msg.timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      ))}
                      {isSending && (
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          padding: '10px 14px',
                          background: '#ffffff',
                          borderRadius: '10px',
                          border: '1px solid #e5e7eb',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                          maxWidth: '140px'
                        }}>
                          <div style={{
                            width: '8px',
                            height: '8px',
                            background: '#6b7280',
                            borderRadius: '50%',
                            animation: 'pulse 1.5s ease-in-out infinite'
                          }} />
                          <span style={{ color: '#6b7280', fontSize: '12px', fontWeight: '500' }}>
                            Typing...
                          </span>
                        </div>
                      )}
                      <div ref={chatEndRef} />
                    </div>

                    {/* Input */}
                    <div style={{ 
                      padding: isMobile ? '14px 16px' : '14px 20px', 
                      borderTop: '1px solid #dde1e6',
                      background: '#ffffff',
                      flexShrink: 0
                    }}>
                      <div style={{ display: 'flex', gap: isMobile ? '8px' : '8px', alignItems: 'center' }}>
                        <input
                          type="text"
                          value={currentMessage}
                          onChange={(e) => setCurrentMessage(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                          placeholder={isMobile ? "Type message..." : "Type your message..."}
                          disabled={isSending}
                          style={{
                            flex: 1,
                            padding: isMobile ? '14px 16px' : '10px 14px',
                            borderRadius: '12px',
                            border: '2px solid #d1d5db',
                            fontSize: isMobile ? '16px' : '13px',
                            outline: 'none',
                            transition: 'all 0.2s',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                            color: '#1f2937',
                            minHeight: isMobile ? '48px' : 'auto',
                            WebkitAppearance: 'none'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#2563eb';
                            e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#d1d5db';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                        <button
                          data-interview-action="true"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            sendMessage();
                          }}
                          onTouchStart={(e) => {
                            e.stopPropagation();
                          }}
                          disabled={isSending || !currentMessage.trim()}
                          style={{
                            padding: isMobile ? '14px 18px' : '10px 18px',
                            background: isSending || !currentMessage.trim() 
                              ? '#e5e7eb' 
                              : '#2563eb',
                            color: isSending || !currentMessage.trim() ? '#9ca3af' : 'white',
                            border: 'none',
                            borderRadius: '12px',
                            fontWeight: '700',
                            cursor: isSending || !currentMessage.trim() ? 'not-allowed' : 'pointer',
                            fontSize: isMobile ? '18px' : '14px',
                            transition: 'all 0.2s',
                            minHeight: isMobile ? '48px' : 'auto',
                            minWidth: isMobile ? '48px' : 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            WebkitTapHighlightColor: 'transparent',
                            touchAction: 'manipulation'
                          }}
                          onMouseEnter={(e) => {
                            if (!isSending && currentMessage.trim()) {
                              e.target.style.background = '#1d4ed8';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSending && currentMessage.trim()) {
                              e.target.style.background = '#2563eb';
                            }
                          }}
                        >
                          {isSending ? '...' : '→'}
                        </button>
                      </div>
                    </div>
                </div>
                )}

                {/* Resize Handle 2: Chat <-> Code Editor - Hidden on mobile */}
                {!isMobile && (
                  <div
                    onMouseDown={() => setIsResizingChat(true)}
                    style={{
                      width: '8px',
                      background: '#e5e7eb',
                      cursor: 'col-resize',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderLeft: '1px solid #d1d5db',
                      borderRight: '1px solid #d1d5db',
                      transition: 'background 0.2s',
                      flexShrink: 0
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#cbd5e1'}
                    onMouseLeave={(e) => e.target.style.background = '#e5e7eb'}
                  >
                    <div style={{
                      width: '3px',
                      height: '40px',
                      background: '#94a3b8',
                      borderRadius: '2px'
                    }} />
                  </div>
                )}

                {/* Right Side - Code Editor (FIXED - doesn't scroll) */}
                {(!isMobile || activeMobileTab === 'editor') && (
                  <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    background: '#1e1e1e',
                    height: isMobile ? 'calc(100% - 48px)' : '100%',
                    overflow: 'hidden',
                    width: isMobile ? '100%' : 'auto',
                    marginTop: isMobile ? '48px' : 0
                  }}>
                  <div style={{
                    padding: isMobile ? '14px 16px' : '10px 20px',
                    background: '#252526',
                    borderBottom: '1px solid #1e1e1e',
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? '10px' : '12px',
                    flexWrap: 'wrap',
                    flexShrink: 0
                  }}>
                    <span style={{ fontSize: isMobile ? '18px' : '16px' }}>💻</span>
                    <h3 style={{ margin: 0, color: '#cccccc', fontSize: isMobile ? '15px' : '13px', fontWeight: '700' }}>
                      Your Solution
                    </h3>
                    <span style={{
                      padding: isMobile ? '6px 12px' : '3px 8px',
                      background: '#3c3c3c',
                      color: '#d4d4d4',
                      borderRadius: '6px',
                      fontSize: isMobile ? '12px' : '11px',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      {language}
                    </span>
                  </div>

                  <div style={{ 
                    flex: 1, 
                    height: '100%',
                    minHeight: 0,
                    willChange: 'contents',
                    transform: 'translateZ(0)'
                  }}>
                    <Editor
                      height="100%"
                      language={language}
                      value={code}
                      onChange={(value) => setCode(value || '')}
                      theme="vs-dark"
                      options={{
                        minimap: { enabled: !isMobile },
                        fontSize: isMobile ? 14 : 14,
                        lineNumbers: 'on',
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                        fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
                        fontLigatures: true,
                        cursorBlinking: 'smooth',
                        cursorSmoothCaretAnimation: true,
                        smoothScrolling: true,
                        padding: { top: 16, bottom: 16 },
                        // Performance optimizations for mobile
                        renderWhitespace: 'none',
                        renderLineHighlight: 'none',
                        occurrencesHighlight: false,
                        selectionHighlight: false,
                        scrollbar: {
                          useShadows: false,
                          verticalScrollbarSize: isMobile ? 8 : 10,
                          horizontalScrollbarSize: isMobile ? 8 : 10,
                        }
                      }}
                      onMount={(editor) => {
                        editorRef.current = editor;
                        // Additional mobile optimizations
                        if (isMobile) {
                          editor.updateOptions({
                            scrollbar: {
                              useShadows: false,
                              verticalScrollbarSize: 8,
                              horizontalScrollbarSize: 8,
                            }
                          });
                        }
                      }}
                    />
                  </div>
                </div>
                )}
              </div>

              {/* Evaluation Loading Overlay */}
              {isEvaluating && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10000,
                  backdropFilter: 'blur(4px)',
                  padding: isMobile ? '20px' : '0'
                }}>
                  <div style={{
                    background: 'white',
                    borderRadius: isMobile ? '20px' : '16px',
                    padding: isMobile ? '32px 24px' : '40px 50px',
                    textAlign: 'center',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                    maxWidth: isMobile ? '90%' : '400px',
                    width: isMobile ? '100%' : 'auto'
                  }}>
                    <div style={{
                      width: isMobile ? '50px' : '60px',
                      height: isMobile ? '50px' : '60px',
                      border: '4px solid #e5e7eb',
                      borderTop: '4px solid #2563eb',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      margin: '0 auto 20px'
                    }} />
                    <h3 style={{
                      margin: '0 0 12px 0',
                      fontSize: isMobile ? '18px' : '20px',
                      fontWeight: '700',
                      color: '#1f2937'
                    }}>
                      Evaluating Your Interview
                    </h3>
                    <p style={{
                      margin: 0,
                      fontSize: isMobile ? '13px' : '14px',
                      color: '#6b7280',
                      lineHeight: '1.6'
                    }}>
                      Our AI is analyzing your code, communication, and problem-solving approach. This may take 15-30 seconds...
                    </p>
                    <div style={{
                      marginTop: '20px',
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '6px'
                    }}>
                      {[0, 1, 2].map((i) => (
                        <div
                          key={i}
                          style={{
                            width: isMobile ? '6px' : '8px',
                            height: isMobile ? '6px' : '8px',
                            background: '#2563eb',
                            borderRadius: '50%',
                            animation: `pulse 1.4s ease-in-out ${i * 0.2}s infinite`
                          }}
                        />
                      ))}
                    </div>
                    <style>{`
                      @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                      }
                      @keyframes pulse {
                        0%, 100% { opacity: 0.3; transform: scale(0.8); }
                        50% { opacity: 1; transform: scale(1.2); }
                      }
                    `}</style>
                  </div>
                </div>
              )}
            </>
          ) : (
            // Evaluation Screen
            <div style={{ 
              padding: isMobile ? '16px' : '40px 20px',
              background: '#f9fafb',
              minHeight: 'calc(100vh - 64px)',
              paddingTop: isMobile ? '80px' : '40px'
            }}>
              <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
                {/* Header */}
                <div style={{
                  background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                  padding: isMobile ? '24px 20px' : '36px',
                  borderRadius: isMobile ? '16px' : '12px',
                  marginBottom: isMobile ? '20px' : '30px',
                  boxShadow: '0 4px 16px rgba(30, 64, 175, 0.2)',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: isMobile ? '48px' : '60px', marginBottom: isMobile ? '8px' : '12px' }}>
                    🎯
                  </div>
                  <h2 style={{ margin: 0, color: 'white', fontSize: isMobile ? '24px' : '32px', fontWeight: 'bold', marginBottom: isMobile ? '6px' : '8px' }}>
                    Interview Complete!
                  </h2>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.95)', fontSize: isMobile ? '14px' : '16px' }}>
                    Here's your detailed performance evaluation
                  </p>
                </div>

                {/* Evaluation Content */}
                <div style={{
                  background: 'white',
                  borderRadius: isMobile ? '16px' : '20px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  overflow: 'hidden',
                  marginBottom: isMobile ? '20px' : '30px'
                }}>
                  {/* Parse and display evaluation sections */}
                  {evaluation && evaluation.split('\n\n').map((section, idx) => {
                    const lines = section.trim().split('\n');
                    
                    if (!section.trim()) return null;
                    
                    return (
                      <div
                        key={idx}
                        style={{
                          padding: isMobile ? '20px 16px' : '30px 40px',
                          borderBottom: idx < evaluation.split('\n\n').length - 1 ? '2px solid #f0f0f0' : 'none',
                          background: idx % 2 === 0 ? 'white' : '#fafbfc'
                        }}
                      >
                        {lines.map((line, lineIdx) => {
                          // Check if it's a header line (has ** or starts with number)
                          if (line.includes('**') || line.match(/^\d+\.\s*\*\*/)) {
                            const cleanLine = line.replace(/\*\*/g, '').trim();
                            const emoji = cleanLine.match(/^(\d+\.)\s*([A-Z\s]+):/);
                            
                            return (
                              <div key={lineIdx} style={{ marginBottom: isMobile ? '12px' : '16px' }}>
                                <h3 style={{
                                  margin: 0,
                                  fontSize: isMobile ? '18px' : '20px',
                                  fontWeight: '700',
                                  color: '#333',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: isMobile ? '10px' : '12px',
                                  paddingBottom: isMobile ? '10px' : '12px',
                                  borderBottom: '3px solid #2563eb',
                                  flexWrap: 'wrap'
                                }}>
                                  <span style={{
                                    width: isMobile ? '32px' : '36px',
                                    height: isMobile ? '32px' : '36px',
                                    background: '#2563eb',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: isMobile ? '14px' : '16px',
                                    fontWeight: 'bold',
                                    flexShrink: 0
                                  }}>
                                    {emoji ? emoji[1].replace('.', '') : idx + 1}
                                  </span>
                                  <span style={{ wordBreak: 'break-word', flex: 1 }}>{cleanLine}</span>
                                </h3>
                              </div>
                            );
                          }
                          
                          // Regular content line
                          if (line.trim()) {
                            // Check for sub-bullets or list items
                            const isBullet = line.trim().startsWith('-') || line.trim().startsWith('•');
                            const cleanLine = isBullet ? line.trim().substring(1).trim() : line.trim();
                            
                            // Check if it contains a score or rating
                            const hasScore = cleanLine.match(/Score:\s*(\d+)\/10/) || cleanLine.match(/(\d+)\/10/);
                            const hasRating = cleanLine.match(/(Pass|Fail|Hire|No Hire|Strong Hire)/i);
                            
                            return (
                              <div
                                key={lineIdx}
                                style={{
                                  fontSize: isMobile ? '14px' : '15px',
                                  lineHeight: isMobile ? '1.7' : '1.8',
                                  color: '#555',
                                  marginLeft: isBullet ? (isMobile ? '16px' : '20px') : '0',
                                  marginBottom: isMobile ? '8px' : '10px',
                                  paddingLeft: isBullet ? (isMobile ? '16px' : '20px') : '0',
                                  position: 'relative',
                                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                  wordBreak: 'break-word'
                                }}
                              >
                                {isBullet && (
                                  <span style={{
                                    position: 'absolute',
                                    left: '0',
                                    top: isMobile ? '6px' : '8px',
                                    width: isMobile ? '5px' : '6px',
                                    height: isMobile ? '5px' : '6px',
                                    background: '#2563eb',
                                    borderRadius: '50%'
                                  }} />
                                )}
                                {hasScore && (
                                  <span style={{
                                    display: 'inline-block',
                                    padding: isMobile ? '3px 10px' : '4px 12px',
                                    background: parseInt(hasScore[1]) >= 7 ? '#d1fae5' : parseInt(hasScore[1]) >= 5 ? '#fed7aa' : '#fecaca',
                                    color: parseInt(hasScore[1]) >= 7 ? '#065f46' : parseInt(hasScore[1]) >= 5 ? '#92400e' : '#991b1b',
                                    borderRadius: isMobile ? '4px' : '6px',
                                    fontWeight: 'bold',
                                    fontSize: isMobile ? '12px' : '14px',
                                    marginRight: isMobile ? '6px' : '8px',
                                    marginBottom: isMobile ? '4px' : '0'
                                  }}>
                                    {hasScore[1]}/10
                                  </span>
                                )}
                                {hasRating && (
                                  <span style={{
                                    display: 'inline-block',
                                    padding: isMobile ? '3px 10px' : '4px 12px',
                                    background: hasRating[1].toLowerCase().includes('hire') && !hasRating[1].toLowerCase().includes('no') ? '#d1fae5' : hasRating[1].toLowerCase() === 'pass' ? '#d1fae5' : '#fecaca',
                                    color: hasRating[1].toLowerCase().includes('hire') && !hasRating[1].toLowerCase().includes('no') ? '#065f46' : hasRating[1].toLowerCase() === 'pass' ? '#065f46' : '#991b1b',
                                    borderRadius: isMobile ? '4px' : '6px',
                                    fontWeight: 'bold',
                                    fontSize: isMobile ? '12px' : '14px',
                                    marginRight: isMobile ? '6px' : '8px',
                                    marginBottom: isMobile ? '4px' : '0'
                                  }}>
                                    {hasRating[1]}
                                  </span>
                                )}
                                <span style={{ fontWeight: hasScore || hasRating ? '600' : '400', display: 'block', marginTop: (hasScore || hasRating) && isMobile ? '4px' : '0' }}>
                                  {cleanLine.replace(/Score:\s*\d+\/10/g, '').replace(/(\d+)\/10/g, '').replace(/(Pass|Fail|Hire|No Hire|Strong Hire)/gi, '').trim()}
                                </span>
                              </div>
                            );
                          }
                          return null;
                        })}
                      </div>
                    );
                  })}
                </div>

                {/* Action Buttons */}
                <div style={{ 
                  display: 'flex', 
                  gap: isMobile ? '12px' : '20px', 
                  justifyContent: 'center',
                  marginBottom: isMobile ? '24px' : '40px',
                  padding: isMobile ? '0' : 0,
                  flexDirection: isMobile ? 'column' : 'row'
                }}>
                  <button
                    onClick={resetInterview}
                    style={{
                      padding: isMobile ? '16px 24px' : '16px 36px',
                      background: '#2563eb',
                      color: 'white',
                      border: 'none',
                      borderRadius: isMobile ? '12px' : '8px',
                      fontSize: isMobile ? '16px' : '16px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: isMobile ? '10px' : '10px',
                      minHeight: isMobile ? '52px' : 'auto',
                      width: isMobile ? '100%' : 'auto',
                      touchAction: 'manipulation',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (!isMobile) {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.background = '#1d4ed8';
                        e.target.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isMobile) {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.background = '#2563eb';
                        e.target.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.3)';
                      }
                    }}
                    onTouchStart={(e) => {
                      e.target.style.background = '#1d4ed8';
                    }}
                    onTouchEnd={(e) => {
                      setTimeout(() => {
                        e.target.style.background = '#2563eb';
                      }, 150);
                    }}
                  >
                    <span style={{ fontSize: isMobile ? '20px' : '20px' }}>🔄</span>
                    <span>{isMobile ? 'Start New Interview' : 'Start New Interview'}</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Login Required Modal */}
      {showLoginPrompt && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[200] p-4"
          style={{ alignItems: isMobile ? 'flex-end' : 'center' }}
          onClick={() => setShowLoginPrompt(false)}
        >
          <div 
            className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl max-w-md w-full border-2 border-purple-400/50 shadow-2xl shadow-purple-500/30 animate-scale-in"
            style={{ 
              padding: isMobile ? '24px' : '32px',
              borderRadius: isMobile ? '20px 20px 0 0' : '16px',
              maxHeight: isMobile ? '90vh' : 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div style={{ fontSize: isMobile ? '48px' : '64px' }} className="mb-4 animate-bounce">🔒</div>
              <h3 style={{ fontSize: isMobile ? '24px' : '32px' }} className="font-bold text-white mb-3">Login Required</h3>
              <p style={{ fontSize: isMobile ? '14px' : '16px' }} className="text-gray-300 leading-relaxed">
                AI Mock Interviews are available for registered users only. Login to practice with our intelligent interviewer and get personalized feedback.
              </p>
            </div>

            <div className="bg-gradient-to-r from-purple-900/40 to-blue-900/40 border border-purple-400/30 rounded-xl mb-6" style={{ padding: isMobile ? '12px' : '16px' }}>
              <div className="flex items-start gap-3">
                <span style={{ fontSize: isMobile ? '24px' : '32px' }}>✨</span>
                <div>
                  <p style={{ fontSize: isMobile ? '12px' : '14px' }} className="text-purple-300 font-bold mb-2">Premium AI Features</p>
                  <ul style={{ fontSize: isMobile ? '11px' : '14px' }} className="text-gray-300 space-y-1">
                    <li>• Real-time code evaluation</li>
                    <li>• Intelligent follow-up questions</li>
                    <li>• Detailed performance reports</li>
                    <li>• Interview history tracking</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex gap-3" style={{ flexDirection: isMobile ? 'column' : 'row' }}>
              <button
                onClick={() => {
                  setShowLoginPrompt(false);
                  setShowAuthModal(true);
                }}
                className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-xl font-bold transition-all duration-300 shadow-lg hover:shadow-purple-500/50 transform hover:scale-105"
                style={{
                  padding: isMobile ? '14px' : '12px 24px',
                  fontSize: isMobile ? '14px' : '16px',
                  minHeight: isMobile ? '48px' : 'auto'
                }}
              >
                🚀 Login Now
              </button>
              <button
                onClick={() => setShowLoginPrompt(false)}
                className="bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-bold transition-all duration-300"
                style={{
                  padding: isMobile ? '14px' : '12px 24px',
                  fontSize: isMobile ? '14px' : '16px',
                  minHeight: isMobile ? '48px' : 'auto',
                  flex: isMobile ? 1 : 'none'
                }}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Warning Modal */}
      {showNavigationWarning && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[10000] flex items-center justify-center p-4" style={{ alignItems: isMobile ? 'flex-end' : 'center' }}>
          <div className="bg-white shadow-2xl max-w-md w-full overflow-hidden animate-scale-in" style={{ borderRadius: isMobile ? '20px 20px 0 0' : '16px' }}>
            {/* Header */}
            <div className="bg-gradient-to-r from-red-600 to-orange-600" style={{ padding: isMobile ? '16px' : '24px' }}>
              <div className="flex items-center gap-3">
                <div className="bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center" style={{ width: isMobile ? '40px' : '48px', height: isMobile ? '40px' : '48px', fontSize: isMobile ? '24px' : '32px' }}>
                  ⚠️
                </div>
                <div>
                  <h3 style={{ fontSize: isMobile ? '18px' : '20px' }} className="font-bold text-white">Interview in Progress</h3>
                  <p style={{ fontSize: isMobile ? '12px' : '14px' }} className="text-red-100">Leaving will lose all progress</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div style={{ padding: isMobile ? '16px' : '24px' }}>
              <p style={{ fontSize: isMobile ? '14px' : '16px' }} className="text-gray-700 leading-relaxed mb-4">
                Are you sure you want to leave? Your interview session is still active and all your progress will be lost if you navigate away.
              </p>

              <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl" style={{ padding: isMobile ? '12px' : '16px' }}>
                <div className="flex items-start gap-2">
                  <span style={{ fontSize: isMobile ? '18px' : '20px' }}>💡</span>
                  <div>
                    <p style={{ fontSize: isMobile ? '12px' : '14px' }} className="font-semibold text-gray-900 mb-1">What you'll lose:</p>
                    <ul style={{ fontSize: isMobile ? '12px' : '14px' }} className="text-gray-700 space-y-1">
                      <li>• Current problem and your code</li>
                      <li>• Interview chat history</li>
                      <li>• Progress and timing data</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 flex gap-3" style={{ padding: isMobile ? '12px 16px' : '16px 24px', flexDirection: isMobile ? 'column' : 'row' }}>
              <button
                onClick={() => {
                  setShowNavigationWarning(false);
                  setPendingNavigation(null);
                }}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white rounded-xl font-bold transition-all duration-300"
                style={{
                  padding: isMobile ? '14px' : '12px 24px',
                  fontSize: isMobile ? '14px' : '16px',
                  minHeight: isMobile ? '48px' : 'auto'
                }}
              >
                🔙 Stay in Interview
              </button>
              <button
                onClick={() => {
                  setShowNavigationWarning(false);
                  if (pendingNavigation) {
                    if (pendingNavigation.type === 'back') {
                      window.history.back();
                    } else if (pendingNavigation.type === 'link' && pendingNavigation.href) {
                      window.location.href = pendingNavigation.href;
                    }
                  }
                  setPendingNavigation(null);
                }}
                className="flex-1 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white rounded-xl font-bold transition-all duration-300"
                style={{
                  padding: isMobile ? '14px' : '12px 24px',
                  fontSize: isMobile ? '14px' : '16px',
                  minHeight: isMobile ? '48px' : 'auto'
                }}
              >
                ⚠️ Leave Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
      />

      {/* Upsell Modal - Show after free interview feedback */}
      {showUpsellModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[10000] flex items-center justify-center p-4">
          <div className="bg-white shadow-2xl max-w-lg w-full rounded-2xl overflow-hidden animate-scale-in">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-4xl">🎉</span>
                <div>
                  <h3 className="text-2xl font-bold">Great job on your first interview!</h3>
                  <p className="text-purple-100 text-sm mt-1">You're building your confidence</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 text-lg mb-4 leading-relaxed">
                Ready to take your interview skills to the next level? Practice makes perfect, and consistent practice builds the confidence you need to ace real interviews.
              </p>
              
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4 mb-4">
                <p className="font-semibold text-gray-900 mb-2">
                  Get {paidPackCredits} more AI mock interviews for just ₹{totalPrice} (incl. GST)
                </p>
                <div className="text-xs text-gray-600 mt-2 pt-2 border-t border-purple-200">
                  <div className="flex justify-between mb-1">
                    <span>Base Price:</span>
                    <span>₹{basePrice}</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span>GST ({gstPercent}%):</span>
                    <span>₹{gstAmount}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-purple-600 pt-1 border-t border-purple-200">
                    <span>Total:</span>
                    <span>₹{totalPrice}</span>
                  </div>
                </div>
                <ul className="text-gray-700 space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 mt-1">✓</span>
                    <span>Build interview confidence through regular practice</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 mt-1">✓</span>
                    <span>Get ready for real interviews with personalized feedback</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 mt-1">✓</span>
                    <span>Practice at your own pace, whenever you're ready</span>
                  </li>
                </ul>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowUpsellModal(false)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl font-semibold py-3 px-4 transition-all"
                >
                  Maybe Later
                </button>
                <button
                  onClick={() => {
                    setShowUpsellModal(false);
                    setShowPaymentModal(true);
                  }}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl font-semibold py-3 px-4 transition-all"
                >
                  Get {paidPackCredits} More Interviews
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal - Show when no credits available */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[10000] flex items-center justify-center p-4">
          <div className="bg-white shadow-2xl max-w-lg w-full rounded-2xl overflow-hidden animate-scale-in">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-4xl">💼</span>
                <div>
                  <h3 className="text-2xl font-bold">Continue Your Interview Practice</h3>
                  <p className="text-purple-100 text-sm mt-1">Build confidence through consistent practice</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 text-lg mb-4 leading-relaxed">
                You've used your free interview! Keep practicing to build the confidence and readiness you need for real interviews.
              </p>
              
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4 mb-4">
                <div className="text-center mb-3">
                  <p className="text-3xl font-bold text-purple-600">₹{basePrice} + GST</p>
                  <p className="text-gray-600 text-sm mb-2">for {paidPackCredits} AI mock interviews</p>
                  <div className="text-xs text-gray-500 space-y-1 pt-2 border-t border-purple-200">
                    <div className="flex justify-between">
                      <span>Base Price:</span>
                      <span>₹{basePrice}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>GST ({gstPercent}%):</span>
                      <span>₹{gstAmount}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-purple-600 pt-1 border-t border-purple-200">
                      <span>Total (incl. GST):</span>
                      <span>₹{totalPrice}</span>
                    </div>
                  </div>
                </div>
                <ul className="text-gray-700 space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 mt-1">✓</span>
                    <span>Practice at your own pace to build confidence</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 mt-1">✓</span>
                    <span>Get personalized feedback to improve your readiness</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 mt-1">✓</span>
                    <span>Prepare for real interviews with realistic practice</span>
                  </li>
                </ul>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl font-semibold py-3 px-4 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const response = await api.post(
                        '/api/ai-assistant/interview/payment/create-order',
                        {},
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      
                      // Initialize Razorpay payment
                      // Amount from backend is already in paise (total including GST)
                      // totalPrice is in rupees for display
                      const totalAmount = response.data.totalPrice || (response.data.amount / 100);
                      const basePrice = response.data.basePrice || 299;
                      const gstAmount = response.data.gstAmount || 54;
                      
                      const options = {
                        key: response.data.keyId,
                        amount: response.data.amount, // Already in paise (total including GST)
                        currency: response.data.currency,
                        name: 'YCode',
                        description: `AI Mock Interview Credits (5 interviews) - ₹${Math.round(totalAmount)} (Base: ₹${basePrice} + GST: ₹${Math.round(gstAmount)})`,
                        order_id: response.data.orderId,
                        handler: async function (paymentResponse) {
                          try {
                            // Verify payment
                            const verifyResponse = await api.post(
                              '/api/ai-assistant/interview/payment/verify',
                              {
                                orderId: paymentResponse.razorpay_order_id,
                                paymentId: paymentResponse.razorpay_payment_id,
                                signature: paymentResponse.razorpay_signature
                              },
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            
                            if (verifyResponse.data.success) {
                              // Reload credits
                              const creditsResponse = await api.get('/api/ai-assistant/interview/credits', {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              setInterviewCredits(creditsResponse.data);
                              setShowPaymentModal(false);
                              alert('Payment successful! You now have 5 interview credits.');
                            }
                          } catch (error) {
                            console.error('Payment verification failed:', error);
                            alert('Payment verification failed. Please contact support.');
                          }
                        },
                        prefill: {
                          name: '',
                          email: '',
                          contact: ''
                        },
                        theme: {
                          color: '#7c3aed'
                        }
                      };
                      
                      const razorpay = new window.Razorpay(options);
                      razorpay.open();
                    } catch (error) {
                      console.error('Failed to create payment order:', error);
                      alert('Failed to initiate payment. Please try again.');
                    }
                  }}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl font-semibold py-3 px-4 transition-all"
                >
                  Pay ₹{totalPrice} (incl. GST)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Warning Popup */}
      {showMobileWarning && isMobile && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-4"
          onClick={() => {
            setShowMobileWarning(false);
            // Don't save to localStorage - show every time interview starts
          }}
        >
          <div 
            className="bg-white rounded-2xl max-w-sm w-full shadow-2xl animate-scale-in"
            style={{ 
              padding: '24px',
              borderRadius: '20px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-4">
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>💻</div>
              <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1f2937', marginBottom: '8px' }}>
                Use Laptop for Best Experience
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6' }}>
                For the best coding experience with our AI Interview, we recommend using a laptop or desktop computer.
              </p>
            </div>
            <button
              onClick={() => {
                setShowMobileWarning(false);
                // Don't save to localStorage - show every time
              }}
              className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-bold text-base transition-all duration-200 shadow-lg"
              style={{
                minHeight: '48px',
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent'
              }}
            >
              Got it! 👍
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }
      `}</style>
      </div>
    </>
  );
};

export default AIInterviewSimulator;

